import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, ROLE_HOME } from "@/lib/api";
import { ErrorBox, Logo, Splash } from "@/components/common";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) return setError("New passwords do not match");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      setUser(data);
      toast.success("Password updated");
      nav(ROLE_HOME[data.role], { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
      <div className="mb-6 flex flex-col items-center text-center">
        <Logo size={88} />
        <h1 className="mt-4 text-2xl font-extrabold tracking-tight">{user.must_change_password ? "Set your new password" : "Change password"}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {user.must_change_password ? "For your security, replace the temporary password issued by GIA." : "Use at least 8 characters with letters and numbers."}
        </p>
      </div>
      <form onSubmit={submit} className="surface space-y-4 p-6" data-testid="change-password-form">
        <label className="block"><span className="label">Current / temporary password</span>
          <input className="field" type="password" value={form.current} onChange={set("current")} required data-testid="current-password-input" /></label>
        <label className="block"><span className="label">New password</span>
          <input className="field" type="password" value={form.next} onChange={set("next")} required minLength={8} data-testid="new-password-input" /></label>
        <label className="block"><span className="label">Confirm new password</span>
          <input className="field" type="password" value={form.confirm} onChange={set("confirm")} required data-testid="confirm-password-input" /></label>
        <ErrorBox error={error} />
        <button className="btn-primary w-full py-3" disabled={busy} data-testid="change-password-submit">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />} Update password
        </button>
        {user.must_change_password ? (
          <button type="button" className="btn-ghost w-full" onClick={async () => { await logout(); nav("/login"); }} data-testid="change-password-logout">Sign out</button>
        ) : (
          <button type="button" className="btn-ghost w-full" onClick={() => nav(-1)} data-testid="change-password-cancel">Cancel</button>
        )}
      </form>
    </div>
  );
}
